import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { CheckCircle, XCircle, Package, Clock, MapPin, Phone, DollarSign, AlertCircle, Map as MapIcon, ChevronUp, ChevronDown, Navigation as NavigationIcon, Loader2 } from 'lucide-react';
import Map, { Marker, NavigationControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import api from '../services/api';
import toast from 'react-hot-toast';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const WAREHOUSE_COORDS = {
    lat: 4.570591,
    lon: -74.095938
};

const BOGOTA_CENTER = '-74.072092,4.710989';

// Simple in-memory geocode cache to avoid redundant API calls
const geocodeCache = {};

const geocodeAddress = async (address) => {
    if (!address || !MAPBOX_TOKEN) return null;
    const cacheKey = address.trim().toLowerCase();
    if (geocodeCache[cacheKey]) return geocodeCache[cacheKey];

    try {
        const query = `${address}, Bogotá, Colombia`;
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&proximity=${BOGOTA_CENTER}&country=co&limit=1&language=es`;
        const res = await fetch(url);
        const data = await res.json();

        if (data.features && data.features.length > 0) {
            const [lng, lat] = data.features[0].center;
            const result = { lat, lng };
            geocodeCache[cacheKey] = result;
            return result;
        }
    } catch (err) {
        console.warn('Geocode error for:', address, err.message);
    }
    return null;
};

const MessengerPendingView = ({ onUpdate }) => {
    const [loading, setLoading] = useState(true);
    const [toAccept, setToAccept] = useState([]);
    const [toRoute, setToRoute] = useState([]);
    const [processing, setProcessing] = useState({});
    const [showMap, setShowMap] = useState(true);
    const [selectedMarkerId, setSelectedMarkerId] = useState(null);
    const [geocodedCoords, setGeocodedCoords] = useState({}); // { orderId: { lat, lng } }
    const [geocoding, setGeocoding] = useState(false);
    const mapRef = useRef();

    const loadPendingOrders = async () => {
        try {
            setLoading(true);
            const response = await api.get('/messenger/pending');

            if (response.data.success) {
                setToAccept(response.data.data.to_accept || []);
                setToRoute(response.data.data.to_route || []);
            }
        } catch (error) {
            console.error('Error loading pending orders:', error);
            toast.error('Error al cargar pedidos pendientes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPendingOrders();
    }, []);

    // Geocode orders that don't have coordinates
    const allOrders = useMemo(() => [...toAccept, ...toRoute], [toAccept, toRoute]);

    const geocodeOrders = useCallback(async () => {
        if (allOrders.length === 0) return;

        const toGeocode = allOrders.filter(o =>
            !o.latitude && !o.longitude && !geocodedCoords[o.id] && o.customer_address
        );

        if (toGeocode.length === 0) return;

        setGeocoding(true);
        const newCoords = { ...geocodedCoords };

        // Geocode in batches of 5 with small delays to avoid rate limits
        for (let i = 0; i < toGeocode.length; i++) {
            const order = toGeocode[i];
            const result = await geocodeAddress(order.customer_address);
            if (result) {
                newCoords[order.id] = result;
            }
            // Small delay between requests
            if (i < toGeocode.length - 1) {
                await new Promise(r => setTimeout(r, 150));
            }
        }

        setGeocodedCoords(newCoords);
        setGeocoding(false);
    }, [allOrders, geocodedCoords]);

    useEffect(() => {
        if (allOrders.length > 0 && MAPBOX_TOKEN) {
            geocodeOrders();
        }
    }, [allOrders.length]); // Only re-run when order count changes

    // Merge backend coords with geocoded coords
    const ordersWithCoords = useMemo(() => {
        return allOrders.map(o => {
            if (o.latitude && o.longitude) return o; // Already has coords from backend
            const gc = geocodedCoords[o.id];
            if (gc) return { ...o, latitude: gc.lat, longitude: gc.lng };
            return o;
        }).filter(o => o.latitude && o.longitude);
    }, [allOrders, geocodedCoords]);

    // Fit map bounds when orders change
    useEffect(() => {
        if (ordersWithCoords.length > 0 && mapRef.current && showMap) {
            try {
                const lats = [WAREHOUSE_COORDS.lat, ...ordersWithCoords.map(o => parseFloat(o.latitude))];
                const lons = [WAREHOUSE_COORDS.lon, ...ordersWithCoords.map(o => parseFloat(o.longitude))];
                const minLat = Math.min(...lats);
                const maxLat = Math.max(...lats);
                const minLon = Math.min(...lons);
                const maxLon = Math.max(...lons);

                setTimeout(() => {
                    mapRef.current?.fitBounds(
                        [[minLon, minLat], [maxLon, maxLat]],
                        { padding: 50, duration: 800 }
                    );
                }, 300);
            } catch (error) {
                console.error('Error fitting bounds:', error);
            }
        }
    }, [ordersWithCoords, showMap]);

    const handleAcceptOrder = async (orderId) => {
        try {
            setProcessing({ ...processing, [orderId]: 'accepting' });
            const response = await api.post(`/messenger/orders/${orderId}/accept`);
            if (response.data.success) {
                toast.success('Pedido aceptado');
                await loadPendingOrders();
                onUpdate?.();
            }
        } catch (error) {
            console.error('Error accepting order:', error);
            toast.error(error.response?.data?.message || 'Error al aceptar pedido');
        } finally {
            setProcessing({ ...processing, [orderId]: null });
        }
    };

    const handleRejectOrder = async (orderId) => {
        const reason = prompt('¿Por qué rechazas este pedido?');
        if (!reason) return;
        try {
            setProcessing({ ...processing, [orderId]: 'rejecting' });
            const response = await api.post(`/messenger/orders/${orderId}/reject`, { reason });
            if (response.data.success) {
                toast.success('Pedido rechazado');
                await loadPendingOrders();
                onUpdate?.();
            }
        } catch (error) {
            console.error('Error rejecting order:', error);
            toast.error(error.response?.data?.message || 'Error al rechazar pedido');
        } finally {
            setProcessing({ ...processing, [orderId]: null });
        }
    };

    // Helper to get coords for an order (from backend or geocoded)
    const getOrderCoords = (order) => {
        if (order.latitude && order.longitude) return { lat: parseFloat(order.latitude), lng: parseFloat(order.longitude) };
        const gc = geocodedCoords[order.id];
        if (gc) return gc;
        return null;
    };

    const OrderCard = ({ order, category, index }) => {
        const isProcessing = processing[order.id];
        const requiresPayment = order.requires_payment && order.payment_amount > 0;
        const isSelected = selectedMarkerId === order.id;
        const coords = getOrderCoords(order);

        return (
            <div
                id={`order-card-${order.id}`}
                className={`bg-white rounded-lg border-2 p-4 hover:shadow-md transition-all cursor-pointer ${isSelected ? 'border-blue-500 shadow-lg ring-2 ring-blue-200' : 'border-gray-200'}`}
                onClick={() => {
                    setSelectedMarkerId(order.id);
                    if (coords && mapRef.current) {
                        mapRef.current.flyTo({ center: [coords.lng, coords.lat], zoom: 14, duration: 800 });
                        if (!showMap) setShowMap(true);
                    }
                }}
            >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                        {coords && (
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-black shadow-sm ${category === 'to_accept' ? 'bg-yellow-500' : 'bg-blue-600'}`}>
                                {index + 1}
                            </div>
                        )}
                        <div>
                            <h3 className="font-bold text-lg text-gray-900">#{order.order_number}</h3>
                            <p className="text-sm text-gray-500">
                                {new Date(order.created_at).toLocaleDateString('es-CO', {
                                    day: 'numeric',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </p>
                        </div>
                    </div>

                    {category === 'to_accept' && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Por Aceptar
                        </span>
                    )}
                    {category === 'to_route' && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            Aceptado
                        </span>
                    )}
                </div>

                {/* Customer Info */}
                <div className="space-y-2 mb-3">
                    <div className="flex items-start gap-2 text-sm">
                        <Package className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="font-medium text-gray-900">{order.customer_name}</span>
                    </div>

                    {order.customer_phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <a href={`tel:${order.customer_phone}`} className="hover:text-blue-600" onClick={e => e.stopPropagation()}>
                                {order.customer_phone}
                            </a>
                        </div>
                    )}

                    <div className="flex items-start gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="leading-tight">{order.customer_address}</span>
                    </div>

                    {requiresPayment && (
                        <div className="flex items-center gap-2 text-sm">
                            <DollarSign className="w-4 h-4 text-green-600" />
                            <span className="font-bold text-green-700">
                                ${parseFloat(order.payment_amount).toLocaleString('es-CO')}
                            </span>
                            <span className="text-gray-500">
                                {order.payment_method === 'efectivo' ? '💵 Efectivo' :
                                    order.payment_method === 'transferencia' ? '🏦 Transfer.' :
                                        '🔀 ' + order.payment_method}
                            </span>
                        </div>
                    )}
                </div>

                {/* Actions */}
                {category === 'to_accept' && (
                    <div className="flex gap-2">
                        <button
                            onClick={(e) => { e.stopPropagation(); handleAcceptOrder(order.id); }}
                            disabled={!!isProcessing}
                            className="flex-1 py-2 px-4 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                            {isProcessing === 'accepting' ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Aceptando...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-4 h-4" />
                                    Aceptar
                                </>
                            )}
                        </button>

                        <button
                            onClick={(e) => { e.stopPropagation(); handleRejectOrder(order.id); }}
                            disabled={!!isProcessing}
                            className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                            {isProcessing === 'rejecting' ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Rechazando...
                                </>
                            ) : (
                                <>
                                    <XCircle className="w-4 h-4" />
                                    Rechazar
                                </>
                            )}
                        </button>
                    </div>
                )}

                {category === 'to_route' && (
                    <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
                        <Clock className="w-4 h-4" />
                        <span className="font-medium">Esperando que logística cree la ruta</span>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    const totalPending = toAccept.length + toRoute.length;

    if (totalPending === 0) {
        return (
            <div className="text-center py-12">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No hay pedidos pendientes</h3>
                <p className="text-gray-500">Todos tus pedidos están enrutados o entregados</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Map Section */}
            {MAPBOX_TOKEN && totalPending > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <button
                        onClick={() => setShowMap(!showMap)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors border-b border-gray-200"
                    >
                        <div className="flex items-center gap-2">
                            <MapIcon className="w-5 h-5 text-blue-600" />
                            <span className="font-bold text-gray-800">
                                Mapa de Pedidos
                            </span>
                            {geocoding ? (
                                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Ubicando...
                                </span>
                            ) : (
                                <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full font-medium">
                                    {ordersWithCoords.length}/{totalPending} ubicaciones
                                </span>
                            )}
                        </div>
                        {showMap ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </button>

                    {showMap && (
                        <div className="relative h-[45vh] min-h-[300px] w-full">
                            <Map
                                ref={mapRef}
                                initialViewState={{
                                    latitude: WAREHOUSE_COORDS.lat,
                                    longitude: WAREHOUSE_COORDS.lon,
                                    zoom: 11
                                }}
                                mapStyle="mapbox://styles/mapbox/streets-v12"
                                mapboxAccessToken={MAPBOX_TOKEN}
                                style={{ width: '100%', height: '100%' }}
                            >
                                <NavigationControl position="top-right" />

                                {/* Warehouse Marker */}
                                <Marker
                                    latitude={WAREHOUSE_COORDS.lat}
                                    longitude={WAREHOUSE_COORDS.lon}
                                    anchor="bottom"
                                >
                                    <div className="relative">
                                        <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center shadow-lg border-4 border-white">
                                            <NavigationIcon className="w-5 h-5 text-white" />
                                        </div>
                                        <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white px-2 py-0.5 rounded shadow-md text-[10px] font-black text-gray-800">
                                            Bodega
                                        </div>
                                    </div>
                                </Marker>

                                {/* Order Markers */}
                                {ordersWithCoords.map((order, idx) => {
                                    const isAccept = order.category === 'to_accept';
                                    const isSelected = selectedMarkerId === order.id;

                                    return (
                                        <Marker
                                            key={order.id}
                                            latitude={parseFloat(order.latitude)}
                                            longitude={parseFloat(order.longitude)}
                                            anchor="bottom"
                                            onClick={(e) => {
                                                e.originalEvent.stopPropagation();
                                                setSelectedMarkerId(order.id);
                                                const card = document.getElementById(`order-card-${order.id}`);
                                                if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                            }}
                                        >
                                            <div className="relative cursor-pointer group">
                                                <div
                                                    className={`flex items-center justify-center shadow-lg border-4 border-white transition-transform group-hover:scale-110 rounded-full w-10 h-10 ${isSelected ? 'scale-125 ring-4 ring-blue-300' : ''
                                                        } ${isAccept ? 'bg-yellow-500' : 'bg-blue-600'}`}
                                                >
                                                    <span className="text-white font-black text-sm">{idx + 1}</span>
                                                </div>

                                                {/* Tooltip */}
                                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 pointer-events-none">
                                                    <div className="bg-white rounded-lg shadow-xl p-3 min-w-[180px] border border-gray-200">
                                                        <div className="text-xs font-black text-gray-900 mb-0.5">#{order.order_number}</div>
                                                        <div className="text-xs text-gray-700 mb-0.5">{order.customer_name}</div>
                                                        <div className="text-[10px] text-gray-500 flex items-start gap-1">
                                                            <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                            <span className="line-clamp-2">{order.customer_address}</span>
                                                        </div>
                                                        <div className={`mt-1 text-[10px] font-bold ${isAccept ? 'text-yellow-700' : 'text-blue-700'}`}>
                                                            {isAccept ? '⏳ Por Aceptar' : '✅ Aceptado'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Marker>
                                    );
                                })}
                            </Map>

                            {/* Legend */}
                            <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg shadow-md px-3 py-2 text-[10px] font-bold space-y-1 border border-gray-200">
                                {toAccept.length > 0 && (
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                                        <span className="text-gray-700">Por Aceptar ({toAccept.length})</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded-full bg-blue-600"></div>
                                    <span className="text-gray-700">Aceptados ({toRoute.length})</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded-full bg-green-600"></div>
                                    <span className="text-gray-700">Bodega</span>
                                </div>
                            </div>

                            {/* Geocoding indicator */}
                            {geocoding && (
                                <div className="absolute top-3 left-3 bg-blue-600 text-white px-3 py-1.5 rounded-lg shadow-lg text-xs font-bold flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Geocodificando direcciones...
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Por Aceptar Section */}
            {toAccept.length > 0 && (
                <div>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                            <Clock className="w-5 h-5 text-yellow-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">
                            Por Aceptar
                            <span className="ml-2 text-sm font-normal text-gray-500">
                                ({toAccept.length} {toAccept.length === 1 ? 'pedido' : 'pedidos'})
                            </span>
                        </h2>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        {toAccept.map((order, idx) => (
                            <OrderCard key={order.id} order={order} category="to_accept" index={idx} />
                        ))}
                    </div>
                </div>
            )}

            {/* Por Enrutar Section */}
            {toRoute.length > 0 && (
                <div>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <Package className="w-5 h-5 text-blue-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">
                            Aceptados - Esperando Ruta
                            <span className="ml-2 text-sm font-normal text-gray-500">
                                ({toRoute.length} {toRoute.length === 1 ? 'pedido' : 'pedidos'})
                            </span>
                        </h2>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-900">
                                <p className="font-semibold mb-1">Estos pedidos ya fueron aceptados</p>
                                <p className="text-blue-700">
                                    El equipo de logística creará la ruta optimizada y confirmará el handoff físico.
                                    Una vez hecho, aparecerán en tu "Ruta Activa".
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        {toRoute.map((order, idx) => (
                            <OrderCard key={order.id} order={order} category="to_route" index={idx} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MessengerPendingView;
